import {
  LINK_LIFETIME_HOURS,
  type LinkEntry,
  type LinkEntryProblem,
  type LinkLifetime,
  type LinkState,
} from '../schemas/link.js';

/**
 * The three rules of a link that depend on NOTHING but arithmetic and a lookup
 * (refonte modulaire §5.1, §11.4).
 *
 * PURE and in `shared` for the reason every rule of this repo ends up here: the
 * server refuses an entry with them and the creation form greys its button with
 * them, and two spellings of one rule would show up as a click that goes
 * through to a 409 nobody expected.
 */

/** One hour, so the two functions below do not each spell 3_600_000. */
const HOUR_MS = 3_600_000;

/**
 * When a link stops opening: its anchor plus its lifetime (§5.1).
 *
 * DERIVED and never stored - the argument is at the head of `schemas/link.ts`.
 * The anchor is the creation for a fresh link, and the instant of the last
 * « Prolonger » afterwards, which is what makes an extension « repartir
 * d'aujourd'hui pour la durée de vie choisie ».
 */
export function linkExpiresAt(lifetimeStartedAt: string, lifetime: LinkLifetime): string {
  const start = new Date(lifetimeStartedAt).getTime();
  return new Date(start + LINK_LIFETIME_HOURS[lifetime] * HOUR_MS).toISOString();
}

/**
 * `active`, `revoked` or `expired`, read off the clock (§5.1).
 *
 * A revocation WINS over an expiry, whatever their order in time: it is the
 * gesture that closed the link, and it is its date the list shows. The boundary
 * is closed - a link whose expiry is exactly now is expired - because the two
 * readings differ by one millisecond and « expired » is the safe one: a door
 * that shuts a tick early turns nobody away who could have been let in a tick
 * later.
 */
export function linkStateOf(
  link: { expiresAt: string; revokedAt: string | null },
  now: Date,
): LinkState {
  if (link.revokedAt !== null) return 'revoked';
  return new Date(link.expiresAt).getTime() <= now.getTime() ? 'expired' : 'active';
}

/** What the base knows about one experience, and a link entry does not. */
export type LinkExperienceState = {
  name: string;
  /** The POINTER, not a count of versions (§4): five versions and no pointer is no production. */
  hasProduction: boolean;
  retiredAt: string | null;
  /**
   * The numbers of the versions the experience holds, for the entries that pin
   * one. Optional, like every fact only a caller that READ the versions can give:
   * a lookup without it claims no `version-unknown`, and the start meets the
   * strict resolution instead.
   */
  versionNumbers?: readonly number[];
};

/** The same seam as `ExperienceReferenceLookup`: one read of the base, handed in. */
export type LinkExperienceLookup = (experienceId: string) => LinkExperienceState | undefined;

/** How the entries of one server are judged. */
export type LinkEntryRules = {
  /**
   * The vitrine (vitrine.md §3.4, V5): every entry is `latest`, and one that is
   * not is refused `pin-not-latest`. False on the atelier, where an entry may
   * pin a precise version.
   */
  readonly latestOnly?: boolean;
};

/**
 * Which entries do not resolve, and why (§5.1, strict resolution of §4).
 *
 * « La création refuse une entrée qui ne résout pas, en nommant l'expérience :
 * une expérience retirée, ou `latest` côté prod sur une expérience jamais
 * passée en production. » It is checked at the creation AND at the
 * modification, for the same reason: a link that can make nobody enter is worth
 * a sentence to the operator rather than a dead screen in the face of somebody
 * who was recruited.
 *
 * Every faulty row is reported, not only the first: the form underlines them
 * all at once, and an operator who fixes three rows one round trip at a time is
 * an operator who stops trusting the form.
 *
 * `latest` on the `dev` side is NEVER refused for want of a pointer:
 * development always sits on a version (§4, « `développement` naît sur la v1 »),
 * so an experience that exists and is not retired resolves under it by
 * construction. A precise version resolves in itself, on either side, as long
 * as the experience holds it.
 */
export function linkEntryProblems(
  entries: readonly LinkEntry[],
  lookup: LinkExperienceLookup,
  rules: LinkEntryRules = {},
): LinkEntryProblem[] {
  const problems: LinkEntryProblem[] = [];

  entries.forEach((entry, index) => {
    const state = lookup(entry.experienceId);
    if (state === undefined) {
      problems.push({
        index,
        code: 'experience-unknown',
        experienceId: entry.experienceId,
        experienceName: null,
      });
      return;
    }
    const named = { index, experienceId: entry.experienceId, experienceName: state.name } as const;
    if (state.retiredAt !== null) {
      problems.push({ ...named, code: 'experience-retired' });
      return;
    }
    if (rules.latestOnly === true && entry.pin !== 'latest') {
      problems.push({ ...named, code: 'pin-not-latest' });
      return;
    }
    if (entry.pin === 'latest') {
      if (entry.side === 'prod' && !state.hasProduction) {
        problems.push({ ...named, code: 'production-empty' });
      }
      return;
    }
    if (state.versionNumbers !== undefined && !state.versionNumbers.includes(entry.pin)) {
      problems.push({ ...named, code: 'version-unknown' });
    }
  });

  return problems;
}
